import { useToast } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { useDebounceFn, useResizeObserver, useRunOnceWhen, useUpdate } from "@nens/react/hooks";

import type { ResizeDirection } from "@/components/rana-resizable";

import { A4_LONG_SIDE_PX, A4_SHORT_SIDE_PX, EXPORT_MAP_ID, MIN_CAPTURE_AREA_SIZE } from "../consts";
import {
  CaptureArea,
  ExportFormat,
  ExportSettings,
  ExportSize,
  PDFOrientation,
} from "../types/index";
import {
  getOrientationCaptureAreaPatch,
  getResizePatchFromDelta,
  resolveCaptureAreaUpdate,
} from "../utils/capture-area-geometry";
import { exportMapSection } from "../utils/export-map-to-screenshot";

const DEFAULT_SETTINGS: ExportSettings = {
  format: "png",
  size: "1x",
  orientation: "portrait",
  fileName: "map-export",
};

const HIDDEN_CAPTURE_AREA: CaptureArea = {
  x: -1000,
  y: -1000,
  width: 0,
  height: 0,
};

export function useRanaMapExport(onClose: () => void) {
  const intl = useIntl();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const { [EXPORT_MAP_ID]: map } = useMap();
  const mapContainer = map?.getContainer();
  const mapContainerRect = mapContainer?.getBoundingClientRect();
  const containerWidth = mapContainerRect?.width ?? 0;
  const containerHeight = mapContainerRect?.height ?? 0;

  useResizeObserver(useDebounceFn(useUpdate(), 100).run, mapContainer);

  const [settings, setSettings] = useState<ExportSettings>(DEFAULT_SETTINGS);
  const [captureArea, setCaptureArea] = useState<CaptureArea>(HIDDEN_CAPTURE_AREA);

  const resetCaptureArea = useCallback(() => {
    const width = containerWidth / 2;
    const height = containerHeight / 1.5;

    setCaptureArea({
      x: Math.max(0, (containerWidth - width) / 2),
      y: Math.max(0, (containerHeight - height) / 2),
      width,
      height,
    });
  }, [containerWidth, containerHeight]);

  useRunOnceWhen(!!map, resetCaptureArea);

  const updateCaptureArea = useCallback(
    (patch: Partial<CaptureArea>, options?: { orientationOverride?: PDFOrientation }) => {
      setCaptureArea((prev) => {
        const next = resolveCaptureAreaUpdate(prev, patch, {
          containerWidth,
          containerHeight,
          minCaptureAreaSize: MIN_CAPTURE_AREA_SIZE,
          format: settings.format,
          orientation: settings.orientation,
          orientationOverride: options?.orientationOverride,
        });

        if (isEqual(next, prev)) {
          return prev;
        }

        return next;
      });
    },
    [containerHeight, containerWidth, settings.format, settings.orientation]
  );

  const updateFileName = useCallback((fileName: string) => {
    setSettings((prev) => ({ ...prev, fileName }));
  }, []);

  const updateExportSize = useCallback((size: ExportSize) => {
    setSettings((prev) => ({ ...prev, size }));
  }, []);

  const updateCaptureAreaSize = useCallback(
    (width: number, height: number) => {
      updateCaptureArea({ width, height });
    },
    [updateCaptureArea]
  );

  const updateCaptureAreaFromDelta = useCallback(
    (
      startCaptureArea: CaptureArea,
      direction: ResizeDirection,
      delta: { width: number; height: number }
    ) => {
      const resizePatch = getResizePatchFromDelta(
        startCaptureArea,
        direction,
        delta,
        settings.format
      );
      updateCaptureArea(resizePatch);
    },
    [settings.format, updateCaptureArea]
  );

  const updateCaptureAreaOrientation = useCallback(
    (orientation: PDFOrientation) => {
      const canvasHeight = map?.getCanvas().clientHeight ?? 0;
      const canvasWidth = map?.getCanvas().clientWidth ?? 0;
      const patch = getOrientationCaptureAreaPatch(
        captureArea,
        orientation,
        canvasWidth,
        canvasHeight
      );

      updateCaptureArea(patch, { orientationOverride: orientation });
    },
    [map, captureArea, updateCaptureArea]
  );

  const updateOrientation = useCallback(
    (orientation: PDFOrientation) => {
      setSettings((prev) => ({ ...prev, orientation }));
      if (settings.format === "pdf") {
        updateCaptureAreaOrientation(orientation);
      }
    },
    [settings.format, updateCaptureAreaOrientation]
  );

  const updateFormat = useCallback(
    (format: ExportFormat) => {
      setSettings((prev) => ({ ...prev, format }));
      if (format === "pdf") {
        updateCaptureAreaOrientation(settings.orientation);
      }
    },
    [settings.orientation, updateCaptureAreaOrientation]
  );

  const startExport = async () => {
    if (!map) {
      toast({
        title: intl.formatMessage({ id: "map.export_error", defaultMessage: "Export Error" }),
        description: intl.formatMessage({
          id: "map.export_map_not_ready",
          defaultMessage: "Map is not ready",
        }),
        status: "error",
        duration: 5000,
      });
      return;
    }

    setIsExporting(true);
    try {
      const sizeMultipliers: Record<ExportSize, number> = { "1x": 1, "2x": 2, "3x": 3 };
      const multiplier = sizeMultipliers[settings.size];

      const isPdf = settings.format === "pdf";
      const pdfWidth = settings.orientation === "landscape" ? A4_LONG_SIDE_PX : A4_SHORT_SIDE_PX;
      const pdfHeight = settings.orientation === "landscape" ? A4_SHORT_SIDE_PX : A4_LONG_SIDE_PX;

      const exportWidth = isPdf ? pdfWidth : Math.round(captureArea.width * multiplier);
      const exportHeight = isPdf ? pdfHeight : Math.round(captureArea.height * multiplier);

      const fullFileName = `${settings.fileName}.${
        settings.format === "jpeg" ? "jpg" : settings.format
      }`;

      await exportMapSection(map, captureArea, {
        width: exportWidth,
        height: exportHeight,
        format: settings.format,
        fileName: fullFileName,
      });

      toast({
        title: intl.formatMessage({
          id: "map.export_success",
          defaultMessage: "Export Successful",
        }),
        status: "success",
        duration: 3000,
      });
      onClose();
    } catch (error) {
      toast({
        title: intl.formatMessage({ id: "map.export_error", defaultMessage: "Export Error" }),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isExporting,
    startExport,
    captureArea,
    updateCaptureArea,
    settings,
    updateFormat,
    updateOrientation,
    updateExportSize,
    updateCaptureAreaSize,
    updateCaptureAreaFromDelta,
    updateFileName,
  };
}
